// Identity: anonymous user id, session id, view id.
//
//   anonymous_id  long-lived first-party id; localStorage with a cookie
//                 fallback when storage is partitioned.
//   session_id    sessionStorage with a sliding inactivity timeout (default
//                 30 min); rotated on resume.
//   view_id       one per page view / SPA route activation. Set by the SPA
//                 collector via newView(); used to stitch events on the
//                 backend (see rum.events.view_id).

import { uuid, supports, now } from './safe.js';

const LS_ANON = 'amb.aid';
const SS_SESSION = 'amb.sid';
const COOKIE_ANON = 'amb_aid';
const ONE_YEAR_S = 365 * 24 * 60 * 60;

let _state = null;
let _cfg = { sessionInactivityMs: 30 * 60 * 1000, cookieDomain: null, cookieSecure: true, cookieSameSite: 'Lax' };

export function initIdentity(cfg) {
  // Skip undefined values — Object.assign would otherwise wipe defaults when
  // the caller passes `{ sessionInactivityMs: undefined }`, which silently
  // disables the inactivity check (every reload creates a fresh session).
  if (cfg) {
    for (const k of Object.keys(cfg)) {
      if (cfg[k] !== undefined) _cfg[k] = cfg[k];
    }
  }

  const anon = loadAnonymousId();
  const sess = loadSession();

  const initialViewId = uuid();
  _state = {
    anonymousId: anon.id,
    isNewUser: anon.isNew,
    sessionId: sess.id,
    sessionStartedAt: sess.startedAt,
    isNewSession: sess.isNew,
    viewId: initialViewId,
    // The view id of the current *document* navigation (initial load or
    // bfcache restore). SPA route changes rotate viewId but not this;
    // collectors whose data describes the document load (web vitals,
    // navigation timing) attribute here, because they often report long
    // after the route — and hence viewId — has moved on.
    documentViewId: initialViewId,
    viewStartedAt: now(),
    userId: ''
  };
  touchSession();
  return _state;
}

export function getIdentity() {
  return _state;
}

export function setUser(userId) {
  if (!_state) return;
  _state.userId = userId || '';
}

// Called on hard nav, SPA route change, and bfcache restore. A bfcache
// restore passes { documentNavigation: true }: web-vitals re-reports the
// restored document's metrics, so documentViewId must move with it — a plain
// SPA route change rotates only viewId.
export function newView(opts) {
  if (!_state) return;
  _state.viewId = uuid();
  if (opts && opts.documentNavigation) _state.documentViewId = _state.viewId;
  _state.viewStartedAt = now();
  // Touch session so the inactivity window restarts.
  touchSession();
  return _state.viewId;
}

export function touchSession() {
  if (!_state) return;
  if (!supports('sessionStorage')) return;
  try {
    sessionStorage.setItem(SS_SESSION, JSON.stringify({
      id: _state.sessionId,
      sa: _state.sessionStartedAt,
      la: now()
    }));
  } catch (_) { /* private mode etc. */ }
}

// Drop is_new_* flags off — backend cares about the value, not stale "is new"
// hints from minutes ago.
export function flushNewFlags() {
  if (!_state) return;
  _state.isNewSession = 0;
  _state.isNewUser = 0;
}

// ---------- internals ----------

function loadAnonymousId() {
  if (supports('storage')) {
    try {
      const v = localStorage.getItem(LS_ANON);
      if (v) return { id: v, isNew: 0 };
      const fresh = uuid();
      localStorage.setItem(LS_ANON, fresh);
      writeCookie(COOKIE_ANON, fresh, ONE_YEAR_S);
      return { id: fresh, isNew: 1 };
    } catch (_) { /* fall through */ }
  }
  // Storage blocked (Safari ITP, third-party context). Try cookie.
  const fromCookie = readCookie(COOKIE_ANON);
  if (fromCookie) return { id: fromCookie, isNew: 0 };
  const fresh = uuid();
  writeCookie(COOKIE_ANON, fresh, ONE_YEAR_S);
  return { id: fresh, isNew: 1 };
}

function loadSession() {
  if (supports('sessionStorage')) {
    try {
      const raw = sessionStorage.getItem(SS_SESSION);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.id && parsed.sa && parsed.la) {
          // Inactivity-based expiry. We check on load so that the same
          // sessionStorage entry resurrected from bfcache after a long
          // background still gets evaluated.
          if (now() - parsed.la <= _cfg.sessionInactivityMs) {
            return { id: parsed.id, startedAt: parsed.sa, isNew: 0 };
          }
        }
      }
    } catch (_) { /* fall through */ }
  }
  return { id: uuid(), startedAt: now(), isNew: 1 };
}

function writeCookie(name, value, maxAgeSec) {
  try {
    let c = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAgeSec}; SameSite=${_cfg.cookieSameSite}`;
    if (_cfg.cookieSecure && location.protocol === 'https:') c += '; Secure';
    if (_cfg.cookieDomain) c += `; Domain=${_cfg.cookieDomain}`;
    document.cookie = c;
  } catch (_) { /* silent */ }
}

function readCookie(name) {
  try {
    const m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.$?*|{}()\[\]\\\/+^]/g, '\\$&') + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  } catch (_) { return null; }
}
